import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../../../ui/dialog";
import { Button } from "../../../../../ui/button";
import { Input } from "../../../../../ui/input";
import { Textarea } from "../../../../../ui/textarea";
import { Label } from "../../../../../ui/label";
import { RadioGroup, RadioGroupItem } from "../../../../../ui/radio-group";
import { Checkbox } from "../../../../../ui/checkbox";

import { Tooltip } from "../../../../../ui/tooltip";
import { InfoIcon, Trash2Icon, PlusIcon, Edit2Icon, Wrench, ChevronDownIcon, ChevronUpIcon, SaveIcon } from "lucide-react";
import { CONVERSATION_TREE_TOOLTIPS } from "../../../utils/tooltips";
import { Badge } from "../../../../../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../../ui/select";
import { MultiSelect } from "../../../../../ui/multi-select";
import { Switch } from "../../../../../ui/switch";
import HighlightedTextArea from "../../HighlightedTextArea";
import { VariableType, DecisionVariable, VariableAssignment, ToolCall } from "../../../utils/types";

interface NodeContentFormProps {
  // Modal props
  isVisible: boolean;
  modalTitle: string;
  onCancel: () => void;
  onOk: () => void;
  isOkDisabled: boolean;
  okButtonText?: string;
  cancelButtonText?: string;

  // Entity type
  entityType: "USER" | "BOT" | "DECISION";
  onEntityTypeChange: (value: "USER" | "BOT" | "DECISION") => void;
  allowedEntityTypes?: ("USER" | "BOT" | "DECISION")[];
  entityTypeTooltips?: Record<string, string>;

  // Identifier
  showIdentifier: boolean;
  identifier: string;
  onIdentifierChange: (value: string) => void;
  identifierRequired?: boolean;
  identifierError?: string;
  identifierPlaceholder?: string;

  // Content
  showContent: boolean;
  content: string;
  onContentChange: (value: string) => void;
  contentPlaceholder?: string;
  contentDisabled?: boolean;
  contentDisabledMessage?: string;

  // Next node connection
  showNextNode?: boolean;
  nextNodeId?: string; // legacy
  nextNodeIds?: string[]; // new multi
  onNextNodeChange?: (nodeId: string) => void; // legacy single
  onNextNodesChange?: (nodeIds: string[]) => void; // new multi
  availableNodes?: Array<{
    id: string;
    label: string;
    entityType: string;
    identifier: string;
  }>;

  // Destination node (for StandaloneModal)
  showDestinationNode?: boolean;
  isDestination?: boolean;
  onDestinationChange?: (value: boolean) => void;

  // Potential start point
  showPotentialStart?: boolean;
  potentialStart?: boolean;
  onPotentialStartChange?: (value: boolean) => void;

  // Tool calls
  showToolCalls?: boolean;
  hasToolCall?: boolean;
  onHasToolCallChange?: (value: boolean) => void;
  toolCalls?: ToolCall[];
  onToolCallsChange?: (toolCalls: ToolCall[]) => void;
  onEditToolCall?: (index: number) => void;

  // BOT content mode (exclusive: either content or tool calls)
  botContentMode?: "content" | "toolcalls";
  onBotContentModeChange?: (mode: "content" | "toolcalls") => void;

  // Variable assignment and creation
  showVariableAssignment?: boolean;
  pendingVariableCreation?: { name: string; type: VariableType } | null;
  onPendingVariableCreationChange?: (pending: { name: string; type: VariableType } | null) => void;
  existingVariables?: DecisionVariable[];
  variableAssignments?: VariableAssignment[];
  onVariableAssignmentsChange?: (assignments: VariableAssignment[]) => void;
  onCreateVariable?: () => void;

  // Variable creation interface
  searchText?: string;
  setSearchText?: (text: string) => void;
  newVariableType?: VariableType;
  setNewVariableType?: (type: VariableType) => void;

  // Custom content (for DecisionRuleBuilder in NodeModal)
  customContent?: React.ReactNode;

  // Additional warnings/messages
  additionalContent?: React.ReactNode;
}

export const NodeContentForm: React.FC<NodeContentFormProps> = ({
  isVisible,
  modalTitle,
  onCancel,
  onOk,
  isOkDisabled,
  okButtonText = "Save",
  cancelButtonText = "Cancel",
  
  entityType,
  onEntityTypeChange,
  allowedEntityTypes = ["USER", "BOT", "DECISION"],
  entityTypeTooltips,
  
  showIdentifier,
  identifier,
  onIdentifierChange,
  identifierRequired = false,
  identifierError,
  identifierPlaceholder = "Type identifier name",
  
  showContent,
  content,
  onContentChange,
  contentPlaceholder = "Enter node content",
  contentDisabled = false,
  contentDisabledMessage,
  
  showNextNode = false,
  nextNodeId,
  nextNodeIds,
  onNextNodeChange,
  onNextNodesChange,
  availableNodes = [],
  
  showDestinationNode = false,
  isDestination = false,
  onDestinationChange,
  
  showPotentialStart = false,
  potentialStart = false,
  onPotentialStartChange,
  
  showToolCalls = false,
  hasToolCall = false,
  onHasToolCallChange,
  toolCalls = [],
  onToolCallsChange,
  onEditToolCall,
  
  botContentMode = "content",
  onBotContentModeChange,
  
  showVariableAssignment = false,
  pendingVariableCreation,
  onPendingVariableCreationChange,
  existingVariables = [],
  variableAssignments = [],
  onVariableAssignmentsChange,
  onCreateVariable,
  
  searchText = "",
  setSearchText,
  newVariableType = "string",
  setNewVariableType,
  
  customContent,
  additionalContent,
}) => {
  const [showCreateVariableForm, setShowCreateVariableForm] = useState(false);
  const [newVariableValue, setNewVariableValue] = useState<string | number | boolean>("");
  const [optionalFieldsExpanded, setOptionalFieldsExpanded] = useState(false);
  const [variableNameError, setVariableNameError] = useState<string>("");
  const [showUnsavedVariableModal, setShowUnsavedVariableModal] = useState(false);

  // Track if we've already set the initial expansion state for this modal session
  const hasSetInitialExpansionRef = useRef(false);

  // Auto-expand optional fields when modal becomes visible if any optional fields are set
  useEffect(() => {
    if (isVisible && !hasSetInitialExpansionRef.current) {
      // Check if any optional fields have values set
      const hasOptionalFieldsSet = 
        potentialStart || // Potential start is set
        isDestination || // Destination node is set
        (variableAssignments && variableAssignments.length > 0); // Variables are assigned
      
      setOptionalFieldsExpanded(hasOptionalFieldsSet);
      hasSetInitialExpansionRef.current = true;
    } else if (!isVisible) {
      // Reset for next time modal opens and reset unsaved variable modal
      hasSetInitialExpansionRef.current = false;
      setShowUnsavedVariableModal(false);
    }
  }, [isVisible, potentialStart, isDestination, variableAssignments]); // Include dependencies but only use them on initial open

  // Check if there are unsaved variable changes
  const hasUnsavedVariableChanges = () => {
    return showCreateVariableForm && (
      searchText?.trim() || 
      (newVariableType === "string" && newVariableValue !== "") ||
      (newVariableType === "number" && newVariableValue !== 0) ||
      (newVariableType === "boolean" && newVariableValue !== true) // default is true
    );
  };

  // Handle save with unsaved variable check
  const handleSaveWithUnsavedCheck = () => {
    if (hasUnsavedVariableChanges()) {
      setShowUnsavedVariableModal(true);
    } else {
      onOk();
    }
  };

  // Handle discarding unsaved changes
  const handleDiscardChanges = () => {
    setShowCreateVariableForm(false);
    setSearchText?.("");
    setNewVariableValue("");
    setVariableNameError("");
    setShowUnsavedVariableModal(false);
    onOk();
  };



  // Validation function for variable names
  const validateVariableName = (name: string): string => {
    if (!name.trim()) {
      return "Variable name is required";
    }
    
    // Check for valid programmatic variable name (letters, numbers, underscores only)
    const validPattern = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
    if (!validPattern.test(name)) {
      return "Variable name must start with a letter or underscore and contain only letters, numbers, and underscores";
    }
    
    return "";
  };

  // Handle variable name input with validation
  const handleVariableNameChange = (value: string) => {
    setSearchText?.(value);
    const error = validateVariableName(value);
    setVariableNameError(error);
  };
  
  const renderVariableInput = (variable: DecisionVariable, value: boolean | string | number, onChange: (val: boolean | string | number) => void) => {
    switch (variable.type) {
      case "boolean":
        return (
          <Select value={value.toString()} onValueChange={(val: string) => onChange(val === "true")}>
            <SelectTrigger className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="true">true</SelectItem>
              <SelectItem value="false">false</SelectItem>
            </SelectContent>
          </Select>
        );
      case "string":
        return (
          <Input
            value={value.toString()}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            placeholder={`Enter ${variable.name}`}
          />
        );
      case "number":
        return (
          <Input
            type="number"
            value={value.toString()}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(parseFloat(e.target.value) || 0)}
            placeholder={`Enter ${variable.name}`}
          />
        );
      default:
        return null;
    }
  };



  return (
    <Dialog open={isVisible} onOpenChange={onCancel}>
      <DialogContent className="sm:max-w-2xl max-h-[98vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-lg font-gc-med text-foreground">
            {modalTitle}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Entity Type Selection */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-sm font-gc-med text-foreground">Entity Type:</Label>
              <Tooltip content={CONVERSATION_TREE_TOOLTIPS.ENTITY_TYPE_LABEL} side="right" immediate={true}>
                <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
              </Tooltip>
            </div>
            <RadioGroup 
              value={entityType} 
              onValueChange={onEntityTypeChange}
              className="flex gap-4"
            >
              {allowedEntityTypes.map((type) => {
                const isDisabled = !allowedEntityTypes.includes(type);
                const hasTooltip = entityTypeTooltips && entityTypeTooltips[type];
                
                const radioButton = (
                  <div key={type} className="flex items-center space-x-2">
                    <RadioGroupItem 
                      value={type} 
                      id={`entity-${type.toLowerCase()}`} 
                      disabled={isDisabled}
                    />
                    <Label 
                      htmlFor={`entity-${type.toLowerCase()}`} 
                      className={`text-sm ${isDisabled ? 'text-muted-foreground' : 'text-foreground'} ${hasTooltip ? 'cursor-help' : ''}`}
                    >
                      {type}
                    </Label>
                  </div>
                );

                return hasTooltip ? (
                  <Tooltip key={type} content={entityTypeTooltips[type]} side="right" delayLong={true}>
                    {radioButton}
                  </Tooltip>
                ) : radioButton;
              })}
            </RadioGroup>
          </div>



          {/* Identifier Section */}
          {showIdentifier && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-gc-med text-foreground">
                  Identifier {identifierRequired && <span className="text-red-500">*</span>}
                </Label>
                <Tooltip content={CONVERSATION_TREE_TOOLTIPS.IDENTIFIER} side="right" immediate={true}>
                  <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
                </Tooltip>
              </div>
              {identifierRequired && (
                <div className="text-xs text-orange-600 mt-1 p-2 bg-orange-50 border border-orange-200 rounded">
                  <InfoIcon className="w-3 h-3 inline mr-1" />
                  Identifier is required for this node type.
                </div>
              )}
              <Input
                value={identifier}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => onIdentifierChange(e.target.value)}
                placeholder={identifierPlaceholder}
                className={`max-w-full overflow-hidden text-ellipsis ${identifierError ? "border-red-500" : ""}`}
                style={{ maxWidth: "100%" }}
              />
              {identifierError && (
                <p className="text-sm text-red-500">{identifierError}</p>
              )}
            </div>
          )}



          {/* Custom Content (for DecisionRuleBuilder) */}
          {customContent}

          {/* Content Section - Dynamic for BOT nodes */}
          {showContent && (
            <>
              {/* Main Content - Always Visible */}
              <div className="space-y-4 mb-4">
              {/* BOT Content Mode Selector */}
              {entityType === "BOT" && showToolCalls && (
                  <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-gc-med text-foreground">BOT Response Type:</Label>
                    <Tooltip content={CONVERSATION_TREE_TOOLTIPS.BOT_CONTENT_MODE} side="right" immediate={true}>
                      <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
                    </Tooltip>
                  </div>
                  <RadioGroup 
                    value={botContentMode} 
                    onValueChange={onBotContentModeChange}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      {toolCalls && toolCalls.length > 0 ? (
                        <Tooltip 
                          content="Content is disabled because this bot has tool calls. Remove tool calls to enable content."
                          side="right"
                          immediate={true}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem 
                              value="content" 
                              id="bot-content" 
                              disabled={true}
                            />
                            <Label 
                              htmlFor="bot-content" 
                              className="text-sm text-muted-foreground cursor-not-allowed"
                            >
                              Content
                            </Label>
                          </div>
                        </Tooltip>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value="content" 
                            id="bot-content" 
                          />
                          <Label 
                            htmlFor="bot-content" 
                            className="text-sm text-foreground cursor-pointer"
                          >
                            Content
                          </Label>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      {content && content.trim().length > 0 ? (
                        <Tooltip 
                          content="Tool Calls are disabled because this bot has content. Clear content to enable tool calls."
                          side="right"
                          immediate={true}
                        >
                          <div className="flex items-center space-x-2">
                            <RadioGroupItem 
                              value="toolcalls" 
                              id="bot-toolcalls" 
                              disabled={true}
                            />
                            <Label 
                              htmlFor="bot-toolcalls" 
                              className="text-sm text-muted-foreground cursor-not-allowed"
                            >
                              Tool Calls
                            </Label>
                          </div>
                        </Tooltip>
                      ) : (
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem 
                            value="toolcalls" 
                            id="bot-toolcalls" 
                          />
                          <Label 
                            htmlFor="bot-toolcalls" 
                            className="text-sm text-foreground cursor-pointer"
                          >
                            Tool Calls
                          </Label>
                        </div>
                      )}
                    </div>
                  </RadioGroup>
                </div>
              )}

                {/* Text Content Input */}
                {(entityType !== "BOT" || botContentMode === "content") && (
                  <div className="min-h-[120px]">
                        <HighlightedTextArea
                          rows={4}
                          value={content}
                          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => onContentChange(e.target.value)}
                          placeholder={contentPlaceholder}
                          disabled={contentDisabled}
                        />
                        
                        {contentDisabled && contentDisabledMessage && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground italic">
                              {contentDisabledMessage}
                            </p>
                          </div>
                        )}
                      </div>
                )}

                {/* Tool Calls Section - When in Tool Calls Mode */}
                {entityType === "BOT" && showToolCalls && botContentMode === "toolcalls" && (
                  <div className="space-y-1 min-h-[120px]">
                    {/* Existing Tool Calls as Cards */}
                    {toolCalls.map((toolCall, index) => (
                      <div key={index} className="p-2 border border-border rounded-md space-y-2 bg-card">
                        {/* Tool Call Header */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Wrench className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm font-gc-med text-foreground">
                              {toolCall.name || "Unnamed Function"}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => onEditToolCall?.(index)}
                              className="gap-2"
                            >
                              <Edit2Icon className="w-3 h-3" />
                              Edit
                            </Button>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => {
                                const updatedToolCalls = toolCalls.filter((_, i) => i !== index);
                                onToolCallsChange?.(updatedToolCalls);
                              }}
                              className="gap-2"
                            >
                              <Trash2Icon className="w-3 h-3" />
                              Remove
                            </Button>
                          </div>
                        </div>

                        {/* Tool Call Content Preview */}
                        {toolCall.name && (
                          <div className="space-y-2">
                            <div>
                              <span className="text-xs text-muted-foreground">Description:</span>
                              <p className="text-sm text-foreground mt-1">
                                {toolCall.description || "No description provided"}
                              </p>
                            </div>
                            {toolCall.parameters && toolCall.parameters.length > 0 && (
                              <div>
                                <span className="text-xs text-muted-foreground">Parameters:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {toolCall.parameters.map((param, paramIndex) => (
                                    <span key={paramIndex} className="text-xs bg-accent px-2 py-1 rounded">
                                      {param.name}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {toolCall.responseVariables && toolCall.responseVariables.length > 0 && (
                              <div>
                                <span className="text-xs text-muted-foreground">Response Variables:</span>
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {toolCall.responseVariables.map((responseVar, rvIndex) => (
                                    <span key={rvIndex} className="text-xs bg-accent px-2 py-1 rounded">
                                      {responseVar.name} ({responseVar.values.length})
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Add Tool Call Button - Full Width */}
                    <button
                      type="button"
                      onClick={() => {
                        const newToolCall: ToolCall = { name: "", description: "", parameters: [], responseVariables: [] };
                        onToolCallsChange?.([...toolCalls, newToolCall]);
                      }}
                      className="w-full p-2 border border-dashed border-maitai-green rounded-md bg-card hover:bg-maitai-green/10 transition-colors flex items-center justify-center gap-2 text-maitai-green hover:text-maitai-green"
                    >
                      <PlusIcon className="w-4 h-4" />
                      <span className="text-sm font-gc-med">Add Tool Call</span>
                    </button>

                    {/* Empty State - Only show when no tool calls */}
                    {toolCalls.length === 0 && (
                      <div className="text-center py-2 text-muted-foreground">
                        <p className="text-xs">No tool calls configured yet.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Next Node Connection */}
              {showNextNode && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label className="text-sm font-gc-med text-foreground">Next Node:</Label>
                    <Tooltip content="Select the node that should follow this one in the conversation flow" side="right" immediate={true}>
                      <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
                    </Tooltip>
                  </div>
                  {isDestination ? (
                    <Tooltip content="Cannot add next node for an end node" side="top" immediate={true}>
                      <div>
                        <Select value={nextNodeId || "none"} onValueChange={(value) => onNextNodeChange?.(value === "none" ? "" : value)} disabled>
                          <SelectTrigger className="opacity-50 cursor-not-allowed">
                            <SelectValue placeholder="Select next node..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              <span className="text-muted-foreground italic">No connection</span>
                            </SelectItem>
                            {availableNodes.map((node) => (
                              <SelectItem key={node.id} value={node.id}>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground">
                                    {node.entityType}
                                  </span>
                                  <span className="font-medium truncate max-w-[500px]" title={node.identifier || node.label}>
                                    {node.identifier || node.label}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </Tooltip>
                  ) : (
                    <>
                      {/* New multiselect for multiple next nodes */}
                      <div className="hidden sm:block">
                        <MultiSelect
                          options={availableNodes.map(n => ({ label: n.identifier || n.label, value: n.id }))}
                          value={nextNodeIds || []}
                          onChange={(vals) => onNextNodesChange?.(vals.map(String))}
                          placeholder="Select one or more next nodes..."
                        />
                      </div>
                      {/* Fallback single select for legacy or when nextNodeIds is undefined */}
                      <div className="sm:hidden">
                        <Select value={nextNodeId || "none"} onValueChange={(value) => onNextNodeChange?.(value === "none" ? "" : value)}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select next node..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              <span className="text-muted-foreground italic">No connection</span>
                            </SelectItem>
                            {availableNodes.map((node) => (
                              <SelectItem key={node.id} value={node.id}>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground">
                                    {node.entityType}
                                  </span>
                                  <span className="font-medium truncate max-w-[500px]" title={node.identifier || node.label}>
                                    {node.identifier || node.label}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Optional Fields - Collapsible Drawer */}
              {(showVariableAssignment || showPotentialStart || showDestinationNode) && (
                <div className="border-t border-border pt-4">
                  <div className={`border border-border rounded-lg transition-all duration-200 ${optionalFieldsExpanded ? 'bg-card/30' : 'bg-card/10'}`}>
                    <button
                      type="button"
                      onClick={() => setOptionalFieldsExpanded(!optionalFieldsExpanded)}
                      className="w-full flex items-center justify-between p-4 hover:bg-accent/20 transition-colors rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Label className="text-sm font-gc-med text-foreground cursor-pointer">Optional Fields</Label>
                        <Tooltip content={CONVERSATION_TREE_TOOLTIPS.OPTIONAL_FIELDS} side="right" immediate={true}>
                          <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
                        </Tooltip>
                      </div>
                      {optionalFieldsExpanded ? (
                        <ChevronUpIcon className="w-4 h-4 text-muted-foreground" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-muted-foreground" />
                      )}
                    </button>

                    {optionalFieldsExpanded && (
                      <div className="px-4 pb-4 border-t border-border/50 bg-card/50 rounded-b-lg">
                        <div className="space-y-6 pt-4">
                          {/* Potential Start Point */}
                          {showPotentialStart && entityType === "BOT" && (
                            <div className="flex items-center space-x-2">
                              <Checkbox 
                                id="potential-start"
                                checked={potentialStart} 
                                onCheckedChange={(checked: boolean) => onPotentialStartChange?.(!!checked)}
                              />
                              <div className="flex items-center gap-2">
                                <Label htmlFor="potential-start" className="text-sm font-gc-reg text-foreground">
                                  Conversation Start Node
                                </Label>
                                <Tooltip content={CONVERSATION_TREE_TOOLTIPS.POTENTIAL_START} side="right" immediate={true}>
                                  <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
                                </Tooltip>
                              </div>
                            </div>
                          )}

                          {/* Destination Node Configuration */}
                          {showDestinationNode && (
                            <div className="flex items-center space-x-2">
                              <Checkbox 
                                id="destination-node"
                                checked={isDestination} 
                                onCheckedChange={(checked: boolean) => onDestinationChange?.(!!checked)}
                              />
                              <div className="flex items-center gap-2">
                                <Label htmlFor="destination-node" className="text-sm font-gc-reg text-foreground">
                                  Conversation End Node
                                </Label>
                                <Tooltip content={CONVERSATION_TREE_TOOLTIPS.DESTINATION_NODE} side="right" immediate={true}>
                                  <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
                                </Tooltip>
                              </div>
                            </div>
                          )}

                          {/* Variable Assignment Section */}
                          {showVariableAssignment && (
                            <div className="space-y-3">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm font-gc-med text-foreground">Variable Assignment</Label>
                                <Tooltip content={CONVERSATION_TREE_TOOLTIPS.VARIABLE_ASSIGNMENT} side="right" immediate={true}>
                                  <InfoIcon className="w-4 h-4 text-maitai-green hover:text-maitai-green/80 transition-colors cursor-help" />
                                </Tooltip>
                              </div>

                          <div className="space-y-1">
                        {/* Existing Variables as Cards - Full Width */}
                            {existingVariables.map((variable) => {
                              const currentAssignment = variableAssignments?.find(a => a.variableName === variable.name);
                              const isAssigned = !!currentAssignment;
                              
                              return (
                            <div key={variable.name} className="p-2 border border-border rounded-md bg-card">
                              <div className="flex items-center gap-3 w-full min-h-[32px]">
                                    <Checkbox
                                      id={`var-${variable.name}`}
                                      checked={isAssigned}
                                      onCheckedChange={(checked) => {
                                        const currentAssignments = variableAssignments || [];
                                        const updatedAssignments = currentAssignments.filter(a => a.variableName !== variable.name);
                                        if (checked) {
                                          // Add assignment with default value based on type
                                          const defaultValue = variable.type === "boolean" ? true : variable.type === "number" ? 0 : "";
                                          updatedAssignments.push({ variableName: variable.name, value: defaultValue });
                                        }
                                        onVariableAssignmentsChange?.(updatedAssignments);
                                      }}
                                    />
                                    <Label 
                                      htmlFor={`var-${variable.name}`} 
                                  className="text-sm font-gc-med text-foreground cursor-pointer min-w-0 flex-1"
                                    >
                                      {variable.name}
                                    </Label>
                                    <Badge variant="outline" className="text-xs shrink-0 w-20 justify-center">
                                      {variable.type}
                                    </Badge>
                                    
                                    {isAssigned && (
                                  <div className="flex-1 min-w-0">
                                        {renderVariableInput(variable, currentAssignment?.value !== undefined ? currentAssignment.value : "", (value) => {
                                          const currentAssignments = variableAssignments || [];
                                          const updatedAssignments = currentAssignments.filter(a => a.variableName !== variable.name);
                                          // Always keep the assignment, even if value is empty, so we can validate it
                                          updatedAssignments.push({ variableName: variable.name, value });
                                          onVariableAssignmentsChange?.(updatedAssignments);
                                        })}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })}

                        {/* Add New Variable Card/Button - Full Width */}
                            {!showCreateVariableForm && (
                              <button
                                type="button"
                                onClick={() => {
                                  setShowCreateVariableForm(true);
                                  setVariableNameError("");
                                }}
                            className="w-full p-2 border border-dashed border-maitai-green rounded-md bg-card hover:bg-maitai-green/10 transition-colors flex items-center justify-center gap-2 text-maitai-green hover:text-maitai-green"
                              >
                                <PlusIcon className="w-4 h-4" />
                                <span className="text-sm font-gc-med">Add New Variable</span>
                              </button>
                            )}

                            {/* Inline Creation Form Card */}
                            {showCreateVariableForm && (
                          <div className="p-2 border border-border rounded-md bg-muted/30">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2 w-full">
                                  <Input
                                    placeholder="Variable name (e.g., user_name)"
                                    value={searchText}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleVariableNameChange(e.target.value)}
                                      className={`text-sm flex-1 min-w-0 ${variableNameError ? 'border-red-500' : ''}`}
                                  />
                                  
                                  <Select value={newVariableType} onValueChange={(type: VariableType) => {
                                    setNewVariableType?.(type);
                                    // Reset value when type changes
                                    if (type === "boolean") {
                                      setNewVariableValue(true);
                                    } else if (type === "number") {
                                      setNewVariableValue(0);
                                    } else {
                                      setNewVariableValue("");
                                    }
                                  }}>
                                    <SelectTrigger className="w-24 shrink-0">
                                      <SelectValue placeholder="Type" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="string">String</SelectItem>
                                      <SelectItem value="number">Number</SelectItem>
                                      <SelectItem value="boolean">Boolean</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  
                              <div className="flex-1 min-w-0">
                                    {newVariableType === "string" && (
                                      <Input
                                        placeholder="Enter initial value"
                                        value={newVariableValue.toString()}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVariableValue(e.target.value)}
                                        className="text-sm"
                                      />
                                    )}
                                    
                                    {newVariableType === "number" && (
                                      <Input
                                        type="number"
                                        placeholder="Enter initial value"
                                        value={newVariableValue.toString()}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewVariableValue(parseFloat(e.target.value) || 0)}
                                        className="text-sm"
                                      />
                                    )}
                                    
                                    {newVariableType === "boolean" && (
                                      <Select value={newVariableValue.toString()} onValueChange={(val: string) => setNewVariableValue(val === "true")}>
                                        <SelectTrigger className="w-full">
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="true">true</SelectItem>
                                          <SelectItem value="false">false</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    )}
                                  </div>

                                  {/* Action buttons inline with inputs */}
                                  <div className="flex items-center gap-1 shrink-0">
                                    <Button
                                      type="button"
                                      variant="outline"
                                      size="sm"
                                      onClick={() => {
                                        setShowCreateVariableForm(false);
                                        setNewVariableValue("");
                                        setSearchText?.("");
                                        setVariableNameError("");
                                      }}
                                      className="p-2 h-8 w-8"
                                    >
                                      <Trash2Icon className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      onClick={() => {
                                        if (searchText && newVariableType && !variableNameError) {
                                          // Create the variable first
                                          onPendingVariableCreationChange?.({ name: searchText, type: newVariableType });
                                          onCreateVariable?.();
                                          
                                          // Then immediately assign the value to this node
                                          const currentAssignments = variableAssignments || [];
                                          const updatedAssignments = currentAssignments.filter(a => a.variableName !== searchText);
                                          updatedAssignments.push({ variableName: searchText, value: newVariableValue });
                                          onVariableAssignmentsChange?.(updatedAssignments);
                                          
                                          // Reset form
                                          setShowCreateVariableForm(false);
                                          setSearchText?.("");
                                          setNewVariableValue("");
                                          setVariableNameError("");
                                        }
                                      }}
                                      disabled={!searchText || !newVariableType || !!variableNameError}
                                      className="p-2 h-8 w-8"
                                    >
                                      <SaveIcon className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                                
                                {/* Error message display */}
                                {variableNameError && (
                                  <div className="text-xs text-red-500 px-1">
                                    {variableNameError}
                                  </div>
                                )}
                                </div>
                              </div>
                            )}

                        {/* Empty State - Only show when no variables and not creating */}
                          {existingVariables.length === 0 && !showCreateVariableForm && (
                            <div className="text-center py-6 text-muted-foreground">
                              <p className="text-sm">No variables available yet.</p>
                              <p className="text-xs mt-1">Create variables in decision nodes first, or add a new one above.</p>
                            </div>
                          )}
                          </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Additional Content */}
          {additionalContent}
        </div>

        {/* Dialog Footer */}
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={onCancel}>
            {cancelButtonText}
          </Button>
          <Button 
            onClick={handleSaveWithUnsavedCheck}
            disabled={isOkDisabled}
            className="bg-foreground hover:bg-foreground/90 text-background"
          >
            {okButtonText}
          </Button>
        </div>
      </DialogContent>

      {/* Unsaved Variable Changes Confirmation Modal */}
      <Dialog open={showUnsavedVariableModal} onOpenChange={setShowUnsavedVariableModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Unsaved Variable Changes</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              You have unsaved changes to a variable. Would you like to save the node without this variable, or go back to finish adding it?
            </p>
          </div>
          <div className="flex justify-end space-x-2">
            <Button 
              variant="outline" 
              onClick={() => setShowUnsavedVariableModal(false)}
            >
              Go Back
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDiscardChanges}
            >
              Discard Variable Changes
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};